import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { Login } from "./Login";

type Operation =
  | "bootstrap"
  | "searches"
  | "run"
  | "run-status"
  | "apify-start"
  | "apify-reconcile"
  | "apify-status"
  | "source-items"
  | "source-details"
  | "discovery"
  | "commit"
  | "fail";

const operations: Array<{
  id: Operation;
  label: string;
  method: "GET" | "POST" | "PUT";
  body: string;
  needsRunId?: boolean;
}> = [
  { id: "bootstrap", label: "Read bootstrap", method: "GET", body: "" },
  {
    id: "searches",
    label: "Synchronize searches",
    method: "PUT",
    body: '{\n  "inventoryComplete": false,\n  "searches": []\n}',
  },
  {
    id: "run",
    label: "Create or resume run",
    method: "POST",
    body: '{\n  "idempotencyKey": "",\n  "requestedCount": 20,\n  "mode": "live",\n  "sourceProvider": "apify"\n}',
  },
  {
    id: "apify-start",
    label: "Start Apify source pass",
    method: "POST",
    body: '{\n  "pass": "remote"\n}',
    needsRunId: true,
  },
  {
    id: "apify-reconcile",
    label: "Reconcile next Apify source",
    method: "POST",
    body: "{}",
    needsRunId: true,
  },
  {
    id: "apify-status",
    label: "Read Apify source status",
    method: "GET",
    body: "",
    needsRunId: true,
  },
  {
    id: "source-items",
    label: "Read pending source summaries",
    method: "GET",
    body: "",
    needsRunId: true,
  },
  {
    id: "source-details",
    label: "Read source descriptions",
    method: "POST",
    body: '{\n  "sourceItemIds": []\n}',
    needsRunId: true,
  },
  {
    id: "run-status",
    label: "Read run",
    method: "GET",
    body: "",
    needsRunId: true,
  },
  {
    id: "discovery",
    label: "Persist discovery",
    method: "PUT",
    body: '{\n  "candidates": [],\n  "checkpoint": {},\n  "summary": {}\n}',
    needsRunId: true,
  },
  {
    id: "commit",
    label: "Commit exactly 20",
    method: "POST",
    body: '{\n  "candidateIds": []\n}',
    needsRunId: true,
  },
  {
    id: "fail",
    label: "Fail without importing",
    method: "POST",
    body: '{\n  "code": "INSUFFICIENT_QUALIFIED_CANDIDATES",\n  "message": "",\n  "checkpoint": {}\n}',
    needsRunId: true,
  },
];

function pathFor(operation: Operation, runId: string): string {
  if (operation === "bootstrap") return "/api/top-ups/bootstrap";
  if (operation === "searches") return "/api/top-ups/searches";
  if (operation === "run") return "/api/top-ups/runs";
  const encodedRunId = encodeURIComponent(runId.trim());
  if (operation === "run-status") return `/api/top-ups/runs/${encodedRunId}`;
  if (operation === "apify-start")
    return `/api/top-ups/runs/${encodedRunId}/sources/apify/start`;
  if (operation === "apify-reconcile")
    return `/api/top-ups/runs/${encodedRunId}/sources/apify/reconcile`;
  if (operation === "apify-status")
    return `/api/top-ups/runs/${encodedRunId}/sources/apify`;
  if (operation === "source-items")
    return `/api/top-ups/runs/${encodedRunId}/source-items?state=pending&limit=100`;
  if (operation === "source-details")
    return `/api/top-ups/runs/${encodedRunId}/source-items/details`;
  if (operation === "discovery")
    return `/api/top-ups/runs/${encodedRunId}/discovery`;
  if (operation === "commit") return `/api/top-ups/runs/${encodedRunId}/commit`;
  return `/api/top-ups/runs/${encodedRunId}/fail`;
}

export function TopUpOperator() {
  const [auth, setAuth] = useState<"loading" | "signed-out" | "signed-in">(
    "loading",
  );
  const [operationId, setOperationId] = useState<Operation>("bootstrap");
  const [runId, setRunId] = useState("");
  const operation = useMemo(
    () => operations.find((candidate) => candidate.id === operationId)!,
    [operationId],
  );
  const [body, setBody] = useState(operation.body);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    api
      .session()
      .then((session) =>
        setAuth(session.authenticated ? "signed-in" : "signed-out"),
      )
      .catch(() => setAuth("signed-out"));
  }, []);

  function chooseOperation(next: Operation) {
    const selected = operations.find((candidate) => candidate.id === next)!;
    setOperationId(next);
    setBody(selected.body);
    setError("");
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (operation.needsRunId && !runId.trim()) {
      setError("A top-up run ID is required for this operation.");
      return;
    }
    let parsedBody: unknown;
    if (operation.method !== "GET") {
      try {
        parsedBody = JSON.parse(body);
      } catch {
        setError("The request body must be valid JSON.");
        return;
      }
    }
    setPending(true);
    setError("");
    setResult("");
    try {
      const response = await fetch(pathFor(operation.id, runId), {
        method: operation.method,
        credentials: "same-origin",
        headers: operation.method === "GET" ? undefined : { "Content-Type": "application/json" },
        body: operation.method === "GET" ? undefined : JSON.stringify(parsedBody),
      });
      const payload = (await response.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      if (!response.ok)
        throw new Error(
          payload?.error?.message ?? `Request failed with ${response.status}.`,
        );
      setResult(JSON.stringify(payload, null, 2));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Request failed.");
    } finally {
      setPending(false);
    }
  }

  if (auth === "loading") return <main className="top-up-operator">Loading Jobs Top-Up operator…</main>;
  if (auth === "signed-out")
    return <Login onAuthenticated={() => setAuth("signed-in")} />;

  return (
    <main className="top-up-operator">
      <header>
        <p className="eyebrow">Codex-only operator transport</p>
        <h1>Jobs Top-Up</h1>
        <p>
          This unlinked console sends structured requests only to the authenticated
          Jobs API. The Worker can start bounded public-listing Apify runs, but this
          console never receives the provider credential, modifies LinkedIn, or commits a partial batch.
        </p>
      </header>
      <form onSubmit={submit}>
        <label>
          Operation
          <select
            value={operationId}
            onChange={(event) => chooseOperation(event.target.value as Operation)}
          >
            {operations.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.label} ({candidate.method})
              </option>
            ))}
          </select>
        </label>
        {operation.needsRunId ? (
          <label>
            Run ID
            <input value={runId} onChange={(event) => setRunId(event.target.value)} />
          </label>
        ) : null}
        {operation.method !== "GET" ? (
          <label>
            JSON body
            <textarea value={body} onChange={(event) => setBody(event.target.value)} rows={18} spellCheck={false} />
          </label>
        ) : null}
        <button type="submit" disabled={pending}>
          {pending ? "Sending…" : `${operation.method} ${pathFor(operation.id, runId)}`}
        </button>
      </form>
      {error ? <p className="operator-error" role="alert">{error}</p> : null}
      {result ? <pre aria-label="API response">{result}</pre> : null}
    </main>
  );
}
